import React from 'react';
import LeadStatusBadge from './LeadStatusBadge';
import HeatLevelIndicator from './HeatLevelIndicator';
import { format } from 'date-fns';

/**
 * LeadCardView Component
 *
 * Mobile-responsive card layout for displaying leads.
 * Optimized for touch interactions on mobile devices (<768px).
 *
 * @param {Array} leads - Array of lead objects with assignments
 * @param {function} onLeadClick - Callback when card is clicked (lead) => void
 * @param {function} onHeatLevelChange - Callback for heat level updates (assignmentId, level) => void
 */
function LeadCardView({ leads, onLeadClick, onHeatLevelChange }) {
  if (!leads || leads.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
        <p className="text-gray-600">Try adjusting your filters or search criteria</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {leads.map((lead) => {
        const latestAssignment = lead.assignments?.[lead.assignments.length - 1];
        const status = latestAssignment?.status || 'new';
        const heatLevel = latestAssignment?.heatLevel;
        const lastActivity = latestAssignment?.lastActivityAt || lead.createdAt;

        return (
          <div
            key={lead.id}
            onClick={() => onLeadClick(lead)}
            className="card hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-tenq-gold bg-white"
          >
            {/* Header Section */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">
                  {lead.name || 'Unknown'}
                </h3>
                {lead.phoneNumber ? (
                  <a
                    href={`tel:+${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm text-tenq-gold hover:text-tenq-gold-dark transition-colors"
                  >
                    {lead.phoneNumber}
                  </a>
                ) : (
                  <span className="text-sm text-gray-400">No phone</span>
                )}
                {lead.email && (
                  <p className="text-xs text-gray-600 truncate mt-1">{lead.email}</p>
                )}
              </div>
              <div className="ml-3">
                <LeadStatusBadge status={status} />
              </div>
            </div>

            {/* Heat Level Section */}
            <div className="mb-3" onClick={(e) => e.stopPropagation()}>
              <HeatLevelIndicator
                level={heatLevel}
                onChange={(level) => {
                  if (onHeatLevelChange && latestAssignment) {
                    onHeatLevelChange(latestAssignment.id, level);
                  }
                }}
                size="sm"
              />
            </div>

            {/* Property & Project Info */}
            {(lead.propertyType || lead.projectType) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {lead.propertyType && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {lead.propertyType.toUpperCase()}
                  </span>
                )}
                {lead.projectType && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    {lead.projectType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                )}
              </div>
            )}

            {/* Budget Section */}
            {lead.budgetMin && (
              <div className="mb-3">
                <span className="text-xs text-gray-600">Budget: </span>
                <span className="text-sm font-medium text-gray-900">
                  RM {parseInt(lead.budgetMin).toLocaleString()}
                  {lead.budgetMax && ` - ${parseInt(lead.budgetMax).toLocaleString()}`}
                </span>
              </div>
            )}

            {/* Platform Badge */}
            {lead.platform && lead.platform !== 'manual' && (
              <div className="mb-3">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
                  </svg>
                  {lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1)}
                </span>
              </div>
            )}

            {/* Footer Section */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <div className="flex items-center text-xs text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {format(new Date(lastActivity), 'MMM dd, yyyy')}
              </div>

              {/* Quick Action Buttons */}
              {lead.phoneNumber && (
                <div className="flex items-center gap-2">
                  <a
                    href={`tel:+${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 text-tenq-gold hover:bg-tenq-gold hover:bg-opacity-10 rounded-full transition-colors"
                    aria-label="Call"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                    </svg>
                  </a>
                  <a
                    href={`https://wa.me/${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 text-green-600 hover:bg-green-600 hover:bg-opacity-10 rounded-full transition-colors"
                    aria-label="WhatsApp"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default LeadCardView;
